import React, { useEffect, useMemo, useState } from 'react';
import { FaHeart, FaStar, FaTimes, FaChevronRight } from 'react-icons/fa';
import { getPropertyImages, FALLBACK_PROPERTY_IMAGE } from '../utils/imageHelper';
import { formatPrice as formatPriceUtil } from '../utils/currencyPreference';
import { getFavoriteId, loadWishlistFavorites, saveWishlistFavorites } from '../utils/wishlistStorage';
import './MapPropertyPreviewCard.css';

const formatDateRange = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return null;
  const opts = { day: 'numeric', month: 'short' };
  const start = checkIn.toLocaleDateString('en-GB', opts);
  const end = checkOut.toLocaleDateString('en-GB', opts);
  return `${start} – ${end}`;
};

const getFavoritesKey = () => {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
      return `favorites_${userObj._id || userObj.id}`;
    }
  } catch {
    /* ignore */
  }
  return 'favorites';
};

const MapPropertyPreviewCard = ({
  hotel,
  checkIn,
  checkOut,
  onClose,
  onViewDetails,
}) => {
  const [imageIndex, setImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  const propertyId = hotel?._id || hotel?.id;
  const images = useMemo(() => {
    const resolved = getPropertyImages(hotel);
    return resolved && resolved.length ? resolved : [FALLBACK_PROPERTY_IMAGE];
  }, [hotel]);
  const hasMultiple = images.length > 1;

  useEffect(() => {
    setImageIndex(0);
  }, [propertyId]);

  useEffect(() => {
    if (!hasMultiple) return undefined;
    const autoSlideTimer = window.setInterval(() => {
      setImageIndex((i) => (i + 1) % images.length);
    }, 4200);

    return () => window.clearInterval(autoSlideTimer);
  }, [hasMultiple, images.length, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    try {
      const favorites = loadWishlistFavorites(getFavoritesKey());
      setIsFavorite(favorites.some((fav) => getFavoriteId(fav) === propertyId));
    } catch {
      setIsFavorite(false);
    }
  }, [propertyId]);

  if (!hotel) return null;

  const {
    name = 'Property',
    city = '',
    address = '',
    type = '',
    rating,
    reviews = 0,
    pricePerNight = 0,
    currency = 'INR',
    policies,
  } = hotel;

  let nights = 1;
  if (checkIn && checkOut) {
    const ms = checkOut.getTime() - checkIn.getTime();
    if (ms > 0) nights = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  }

  const totalPrice = (pricePerNight || 0) * nights;
  const dateRange = formatDateRange(checkIn, checkOut);
  const areaLabel = city || address?.split(',')[0] || 'this area';
  const typeLabel = type ? String(type) : 'Place to stay';
  const headline = `${typeLabel} in ${areaLabel}`;
  const subtitle = name.length > 42 ? `${name.slice(0, 42)}…` : name;

  const cancellationNote =
    policies?.cancellationPolicy &&
    String(policies.cancellationPolicy).toLowerCase().includes('free')
      ? 'Free cancellation'
      : null;

  const ratingLabel =
    reviews > 0 && rating != null
      ? Number(rating).toFixed(1)
      : 'New';
  const inventorySource = hotel.source === 'api' ? 'api' : 'manual';

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    try {
      const key = getFavoritesKey();
      const favorites = loadWishlistFavorites(key);
      const idx = favorites.findIndex((fav) => getFavoriteId(fav) === propertyId);
      if (idx >= 0) {
        favorites.splice(idx, 1);
        setIsFavorite(false);
      } else {
        favorites.push({ ...hotel, _id: propertyId, id: propertyId });
        setIsFavorite(true);
      }
      saveWishlistFavorites(key, favorites);
    } catch {
      /* ignore */
    }
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setImageIndex((i) => (i + 1) % images.length);
  };

  const handleOpenDetails = () => {
    onViewDetails?.(hotel);
  };

  return (
    <article
      className="map-preview-card"
      role="dialog"
      aria-label={`Preview: ${name}`}
      onClick={handleOpenDetails}
    >
      <div className="map-preview-card__media">
        <div className="map-preview-card__carousel">
          <div
            className="map-preview-card__track"
            style={{ transform: `translateX(-${imageIndex * 100}%)` }}
          >
            {images.map((img, idx) => (
              <img
                key={`${propertyId || name}-${idx}`}
                src={img || FALLBACK_PROPERTY_IMAGE}
                alt={name}
                className="map-preview-card__img"
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_PROPERTY_IMAGE;
                }}
              />
            ))}
          </div>
        </div>
        <span
          className={`map-preview-card__inventory-badge ${
            inventorySource === 'api'
              ? 'map-preview-card__inventory-badge--api'
              : 'map-preview-card__inventory-badge--manual'
          }`}
        >
          {inventorySource === 'api' ? '⚡ Live Availability' : '🏨 Partner Stay'}
        </span>
        <button
          type="button"
          className="map-preview-card__close"
          aria-label="Close preview"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
        >
          <FaTimes />
        </button>
        <button
          type="button"
          className={`map-preview-card__fav ${isFavorite ? 'active' : ''}`}
          aria-label={isFavorite ? 'Remove from wishlist' : 'Save to wishlist'}
          onClick={handleToggleFavorite}
        >
          <FaHeart />
        </button>
        {hasMultiple && (
          <>
            <button
              type="button"
              className="map-preview-card__next"
              aria-label="Next photo"
              onClick={handleNextImage}
            >
              <FaChevronRight />
            </button>
            <div className="map-preview-card__dots" aria-hidden>
              {images.slice(0, 5).map((_, idx) => (
                <span
                  key={idx}
                  className={`map-preview-card__dot ${idx === imageIndex % 5 ? 'active' : ''}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="map-preview-card__body">
        <div className="map-preview-card__headline">
          <h3 className="map-preview-card__title">{headline}</h3>
          <span className="map-preview-card__rating">
            <FaStar aria-hidden />
            {ratingLabel}
          </span>
        </div>
        <p className="map-preview-card__subtitle">{subtitle}</p>
        {dateRange && <p className="map-preview-card__dates">{dateRange}</p>}
        <p className="map-preview-card__price">
          {pricePerNight > 0
            ? `${formatPriceUtil(totalPrice, currency)} for ${nights} night${nights !== 1 ? 's' : ''}`
            : 'Price on request'}
        </p>
        {cancellationNote && (
          <span className="map-preview-card__badge">{cancellationNote}</span>
        )}
      </div>
    </article>
  );
};

export default MapPropertyPreviewCard;
